import 'normalize.css';
import './App.css';

import { useState, useEffect } from 'react';

function MathGameForm(props) {
  const [formData, setFormData] = useState({ low: 3, high: 6, count: 10, operation: "addition" });

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    props.onSubmit(formData);
  };

  const inputStyle = {
    width: "100%",
    padding: "0.75rem",
    fontSize: "1.2rem",
    borderRadius: "8px",
    border: "2px solid #007bff",
    outline: "none",
    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
    textAlign: "center",
    transition: "all 0.2s ease",
    backgroundColor: "#f0f8ff",
  };

  const inputFocusStyle = {
    transform: "scale(1.05)",
    borderColor: "#28a745",
    backgroundColor: "#e0fff0",
    boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
  };

  const containerStyle = { maxWidth: "400px", margin: "2rem auto", padding: "1rem", border: "1px solid #ddd", borderRadius: "12px", backgroundColor: "#fffde7" };
  const formStyle = { display: "flex", flexDirection: "column", gap: "1rem" };

  const opButtonStyle = (op) => {
    const selected = formData.operation === op;
    const baseColors = { addition: "#28a745", subtraction: "#dc3545" };
    const textColor = selected ? "white" : baseColors[op];
    const background = selected ? baseColors[op] : "white";
    return {
      padding: "0.75rem 2rem",
      border: `2px solid ${baseColors[op]}`,
      borderRadius: "12px",
      background,
      color: textColor,
      fontWeight: "bold",
      cursor: "pointer",
      transition: "all 0.3s ease",
      fontSize: "1.1rem",
    };
  };

  return (
    <div style={containerStyle}>
      <form onSubmit={handleSubmit} style={formStyle}>
        {[
          { id: "low", label: "First number up to", min: 1 },
          { id: "high", label: "Second number up to", min: 1 },
          { id: "count", label: "Number of problems", min: 1 },
        ].map(({ id, label, min }) => (
          <div key={id}>
            <label htmlFor={id} style={{ fontSize: "1.1rem", fontWeight: "bold" }}>{label + ":"}</label>
            <input
              id={id}
              name={id}
              type="number"
              value={formData[id]}
              onChange={handleChange}
              min={min}
              style={inputStyle}
              onFocus={(e) => Object.assign(e.target.style, inputFocusStyle)}
              onBlur={(e) => Object.assign(e.target.style, inputStyle)}
            />
          </div>
        ))}

        <div>
          <label style={{ fontSize: "1.1rem", fontWeight: "bold" }}>Operation</label>
          <div style={{ display: "flex", justifyContent: "center", gap: "1rem", marginTop: "0.5rem" }}>
            {["addition", "subtraction"].map((op) => (
              <button
                key={op}
                type="button"
                onClick={() => setFormData({ ...formData, operation: op })}
                style={opButtonStyle(op)}
              >
                {op === "addition" ? "➕ Addition" : "➖ Subtraction"}
              </button>
            ))}
          </div>
        </div>

        <button
          type="submit"
          style={{
            padding: "0.75rem",
            border: "none",
            borderRadius: "12px",
            background: "#007bff",
            color: "white",
            fontWeight: "bold",
            marginTop: "1rem",
            fontSize: "1.2rem",
            transition: "background 0.3s ease",
          }}
          onMouseOver={e => (e.currentTarget.style.background = "#0056b3")}
          onMouseOut={e => (e.currentTarget.style.background = "#007bff")}
        >
          Start Game 🎉
        </button>
      </form>
    </div>
  );
}

function generateProblems(low, high, count, operation) {
  const problems = [];

  // Generate all valid grade 1 problems
  for (let a = 1; a <= high; a++) {
    for (let b = 1; b <= low; b++) {
      if (operation === "subtraction" && (a == b || a - b < 0)) continue; // no negatives
      problems.push({ a, b, correctAnswer: operation === "addition" ? a + b : a - b });
    }
  }

  console.log("universe has", problems.length, "possible problems");

  const result = [];
  const totalUnique = problems.length;

  if (totalUnique === 0) return []; // edge case, no valid problems

  while (result.length < count) {
    // Shuffle the full problem list
    for (let i = problems.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [problems[i], problems[j]] = [problems[j], problems[i]];
    }

    // Add problems until we reach requested count
    const remaining = count - result.length;
    result.push(...problems.slice(0, remaining));
  }

  return result;
}

function MathGame({ config }) {
  const { low, high, count, operation } = config;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [answer, setAnswer] = useState("");
  const [attempts, setAttempts] = useState(0);
  const [score, setScore] = useState(0);
  const [showCongrats, setShowCongrats] = useState(false);
  const [finished, setFinished] = useState(false);
  const [emojiParticles, setEmojiParticles] = useState([]);
  const [problemsList, setProblemsList] = useState([]);
  const [showAnswer, setShowAnswer] = useState(false);
  const [previousAnswers, setPreviousAnswers] = useState([]);
  const [shakeEffect, setShakeEffect] = useState(false);
  const [tryAgainMessage, setTryAgainMessage] = useState(false);

  const interactionDisabled = showCongrats || showAnswer;

  useEffect(() => {
    const list = generateProblems(low, high, count, operation);
    setProblemsList(list);
    setCurrentIndex(0);
    setScore(0);
    setFinished(false);
    setEmojiParticles([]);
  }, [low, high, count, operation]);


  useEffect(() => {
    if (currentIndex < count) {
      setAnswer("");
      setAttempts(0);
      setShowAnswer(false); // Reset showAnswer when moving to the next problem
      setPreviousAnswers([]); // Reset previous answers for the new problem
      setEmojiParticles([]);
    } else {
      setFinished(true);
    }
  }, [currentIndex]);

  const problem = problemsList[currentIndex];


  const handleSubmit = (e) => {
    e.preventDefault();
    if (!problem) return;

    const numericAnswer = parseInt(answer, 10);

    if (previousAnswers.includes(numericAnswer)) {
      alert("You already tried this answer. Please enter a different one.");
      return;
    }

    setPreviousAnswers((prev) => [...prev, numericAnswer]);

    if (numericAnswer === problem.correctAnswer) {
      setScore((s) => s + 1);
      setTryAgainMessage(false); // Reset try again message
      // spawn floating emojis
      setEmojiParticles(Array.from({ length: 15 }, (_, i) => ({
        id: i,
        left: Math.random() * 80 + 10 + "%",
        top: "100%",
        emoji: ["🎉", "🌟", "✨", "🎈"][Math.floor(Math.random() * 4)],
        rotation: Math.random() * 360,
        delay: Math.random() * 0.5,
      })));
      setShowCongrats(true);
      setTimeout(() => {
        setShowCongrats(false);
        setCurrentIndex((i) => i + 1);
      }, 2000);
    } else {
      setShakeEffect(true); // Trigger shake effect
      setTryAgainMessage(true); // Show try again message
      setTimeout(() => setShakeEffect(false), 500); // Remove shake effect after 500ms

      if (attempts + 1 >= 3) {
        setShowAnswer(true); // Show the correct answer
      } else {
        setAttempts((a) => a + 1);
      }
    }
  };

  const handleNext = () => {
    setCurrentIndex((i) => i + 1);
    setShowAnswer(false); // Reset showAnswer when moving to the next problem
  };

  if (finished) {
    return (
      <div style={{ textAlign: "center", padding: "2rem", fontSize: "1.5rem", position: "relative", overflow: "hidden" }}>
        <h1 style={{ fontSize: "3rem", color: "#ff4081" }}>🎉 Game Over! 🎉</h1>
        <p style={{ fontSize: "1.8rem", color: "#ff9800" }}>Your score: {score} / {count} ⭐🎈</p>
        <Fireworks />
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: "500px",
      margin: "2rem auto",
      padding: "2rem",
      textAlign: "center",
      borderRadius: "16px",
      background: "#fffde7",
      boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
      fontFamily: "'Comic Sans MS', sans-serif",
      position: "relative",
      overflow: "hidden"
    }}>
      <h2 style={{ fontSize: "2.5rem", color: "#ff5722" }}>Problem {currentIndex + 1} / {count}</h2>

      {problem && (
        <div style={{
          fontSize: "3rem",
          background: "#ffecb3",
          margin: "1rem 0",
          padding: "1.5rem",
          borderRadius: "16px",
          boxShadow: "0 6px 15px rgba(0,0,0,0.1)",
          position: "relative",
          animation: shakeEffect ? "shake 0.5s ease" : "none", // Apply shake animation
        }}>
          {problem.a} {operation === "addition" ? "+" : "-"} {problem.b} = {showAnswer ? (
            <span style={{ color: "#f44336", fontWeight: "bold" }}>{problem.correctAnswer}</span>
          ) : "?"}
        </div>
      )}

      {tryAgainMessage && !showAnswer && (
        <p style={{ color: "#f44336", fontSize: "1.2rem", marginTop: "0.5rem" }}>
          Try Again!
        </p>
      )}

      <form onSubmit={handleSubmit} style={{ marginTop: "1rem" }}>
        <input
          type="number"
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          style={{
            width: "120px",
            padding: "0.75rem",
            fontSize: "1.8rem",
            borderRadius: "12px",
            border: "3px solid #ff9800",
            textAlign: "center",
            outline: "none",
            boxShadow: "0 3px 7px rgba(0,0,0,0.15)",
            transition: "transform 0.2s ease, box-shadow 0.2s ease"
          }}
          onFocus={(e) => Object.assign(e.target.style, { transform: "scale(1.1)", boxShadow: "0 6px 15px rgba(0,0,0,0.3)" })}
          onBlur={(e) => Object.assign(e.target.style, { transform: "scale(1)", boxShadow: "0 3px 7px rgba(0,0,0,0.15)" })}
          disabled={interactionDisabled} // <-- disable input during congrats or answer display
        />
        <br />
        <button
          type="submit"
          style={{
            marginTop: "1rem",
            padding: "0.75rem 2rem",
            fontSize: "1.4rem",
            borderRadius: "16px",
            border: "none",
            background: interactionDisabled ? "#d3d3d3" : "#ff9800", // Gray out when disabled
            color: interactionDisabled ? "#a9a9a9" : "white", // Adjust text color when disabled
            fontWeight: "bold",
            cursor: interactionDisabled ? "not-allowed" : "pointer", // Show not-allowed cursor when disabled
            transition: "transform 0.2s ease",
          }}
          onMouseOver={(e) => { if (!interactionDisabled) e.currentTarget.style.transform = "scale(1.1)"; }}
          onMouseOut={(e) => e.currentTarget.style.transform = "scale(1)"}
          disabled={interactionDisabled} // <-- disable button during congrats or answer display
        >
          ✅ Submit
        </button>
      </form>
      <p style={{ marginTop: "0.5rem", fontSize: "1.2rem", color: "#f44336" }}>Attempts left: {3 - attempts}</p>

      {showAnswer && (
        <button
          onClick={handleNext}
          style={{
            marginTop: "1rem",
            padding: "0.75rem 2rem",
            fontSize: "1.4rem",
            borderRadius: "16px",
            border: "none",
            background: "#007bff",
            color: "white",
            fontWeight: "bold",
            cursor: "pointer",
            transition: "transform 0.2s ease",
          }}
          onMouseOver={(e) => e.currentTarget.style.transform = "scale(1.1)"}
          onMouseOut={(e) => e.currentTarget.style.transform = "scale(1)"}
        >
          Next ➡️
        </button>
      )}

      {showCongrats && emojiParticles.map(p => (
        <span key={p.id} style={{
          position: "absolute",
          left: p.left,
          top: p.top,
          fontSize: "2rem",
          transform: `rotate(${p.rotation}deg)`,
          animation: `floatUp 1.5s ease-out forwards`,
          animationDelay: `${p.delay}s`
        }}>
          {p.emoji}
        </span>
      ))}

      {/* simple CSS keyframe animation */}
      <style>
        {`
          @keyframes floatUp {
            0% { transform: translateY(0) rotate(0deg); opacity: 1; }
            100% { transform: translateY(-150px) rotate(360deg); opacity: 0; }
          }
          @keyframes shake {
            0%, 100% { transform: translateX(0); }
            25% { transform: translateX(-10px); }
            50% { transform: translateX(10px); }
            75% { transform: translateX(-10px); }
          }
        `}
      </style>
    </div>
  );
}

function Fireworks() {
  const particles = Array.from({ length: 40 });
  const colors = ["#ff4d4d", "#ffd633", "#4dff4d", "#33ccff", "#ff66cc", "#ffffff"];

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        overflow: "hidden",
      }}
    >
      {particles.map((_, i) => {
        const angle = Math.random() * 2 * Math.PI; // radians
        const distance = 100 + Math.random() * 80; // outward spread
        const x = Math.cos(angle) * distance;
        const y = Math.sin(angle) * distance;
        const color = colors[Math.floor(Math.random() * colors.length)];
        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              width: "2px",
              height: "10px",
              backgroundColor: color,
              borderRadius: "2px",
              transform: `translate(-50%, -50%)`,
              animation: `shoot-${i} 1.2s ease-out forwards`,
            }}
          >
            <style>
              {`
                @keyframes shoot-${i} {
                  from {
                    opacity: 1;
                    transform: translate(-50%, -50%) translate(0, 0) rotate(${angle}rad);
                  }
                  to {
                    opacity: 0;
                    transform: translate(-50%, -50%) translate(${x}px, ${y}px) rotate(${angle}rad);
                  }
                }
              `}
            </style>
          </div>
        );
      })}
    </div>
  );
}

function App() {
  const [config, setConfig] = useState(null);

  return (
    <div className="App">
      {config == null && <MathGameForm onSubmit={setConfig} />}
      {config != null && <MathGame config={config} />}
    </div>
  );
}

export default App;
